import { useEffect, useRef, useState } from 'react'
import { gsap } from 'gsap'
import './PhotographyWindow.css'
import { ALBUMS, PHOTOGRAPHY_META, type Album, type Photo } from '@/data/photography'

const CONFIG = {
  timeZone: 'Europe/Zagreb',
  timeUpdateInterval: 1000,
}

export function PhotographyWindow() {
  const containerRef = useRef<HTMLDivElement>(null)
  const backgroundImageRef = useRef<HTMLDivElement>(null)
  const projectItemsRef = useRef<(HTMLLIElement | null)[]>([])
  const originalTextsRef = useRef<Map<HTMLLIElement, string[]>>(new Map())
  const currentActiveIndexRef = useRef(-1)
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const idleAnimationRef = useRef<gsap.core.Timeline | null>(null)
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [currentTime, setCurrentTime] = useState('')
  const [selectedAlbum, setSelectedAlbum] = useState<Album | null>(null)

  const items = selectedAlbum 
    ? selectedAlbum.photos.map((p) => ({
        image: p.image,
        col1: p.title,
        col2: p.location,
        col3: p.camera,
        col4: selectedAlbum.category,
        col5: selectedAlbum.year,
        isAlbum: false,
      }))
    : ALBUMS.map((a) => ({
        image: a.coverImage,
        col1: a.title,
        col2: a.location,
        col3: a.category,
        col4: `${a.photos.length} PHOTOS`,
        col5: a.year,
        isAlbum: true,
        album: a,
      }))

  // Preload images
  useEffect(() => {
    ALBUMS.forEach((album) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.src = album.coverImage
      
      album.photos.forEach((photo) => {
        const pImg = new Image()
        pImg.crossOrigin = 'anonymous'
        pImg.src = photo.image
      })
    })
  }, [])

  // Time display
  useEffect(() => {
    const updateTime = () => {
      const now = new Date()
      const options: Intl.DateTimeFormatOptions = {
        timeZone: CONFIG.timeZone,
        hour12: true,
        hour: 'numeric',
        minute: 'numeric',
      }
      const formatter = new Intl.DateTimeFormat('en-US', options)
      const parts = formatter.formatToParts(now)
      const hours = parts.find((part) => part.type === 'hour')?.value || ''
      const minutes = parts.find((part) => part.type === 'minute')?.value || ''
      const dayPeriod = parts.find((part) => part.type === 'dayPeriod')?.value || ''
      setCurrentTime(`${hours}:${minutes} ${dayPeriod}`)
    }

    updateTime()
    const interval = setInterval(updateTime, CONFIG.timeUpdateInterval)
    return () => clearInterval(interval)
  }, [])

  // Store original texts
  useEffect(() => {
    // Trim projectItemsRef array to match items length
    projectItemsRef.current = projectItemsRef.current.slice(0, items.length)
    
    // Reset map
    originalTextsRef.current.clear()
    projectItemsRef.current.forEach((item) => {
      if (item) {
        const textElements = item.querySelectorAll('.hover-text')
        const texts = Array.from(textElements).map((el) => el.textContent || '')
        originalTextsRef.current.set(item, texts)
      }
    })
  }, [items])

  // Idle animation
  const startIdleAnimation = () => {
    if (idleAnimationRef.current) return

    const items = projectItemsRef.current.filter(Boolean) as HTMLLIElement[]
    if (items.length === 0) return

    idleAnimationRef.current = gsap.timeline({
      repeat: -1,
      repeatDelay: 2,
    })

    const columnSelectors = ['.title', '.location', '.category', '.camera', '.year']
    const totalRows = items.length
    const columnStartDelay = 0.25
    const rowDelay = 0.05
    const hideShowGap = totalRows * rowDelay * 0.5

    // Counter animation
    items.forEach((item, rowIndex) => {
      const hideTime = rowIndex * rowDelay
      const showTime = hideShowGap + rowIndex * rowDelay

      idleAnimationRef.current?.call(
        () => item.classList.add('counter-hidden'),
        [],
        hideTime
      )
      idleAnimationRef.current?.call(
        () => item.classList.remove('counter-hidden'),
        [],
        showTime
      )
    })

    // Column animations
    columnSelectors.forEach((selector, columnIndex) => {
      const columnStart = (columnIndex + 1) * columnStartDelay

      items.forEach((item, rowIndex) => {
        const element = item.querySelector(selector)
        if (!element) return

        const hideTime = columnStart + rowIndex * rowDelay
        const showTime = columnStart + hideShowGap + rowIndex * rowDelay

        idleAnimationRef.current?.to(
          element,
          { duration: 0.1, opacity: 0.05, ease: 'power2.inOut' },
          hideTime
        )
        idleAnimationRef.current?.to(
          element,
          { duration: 0.1, opacity: 1, ease: 'power2.inOut' },
          showTime
        )
      })
    })
  }

  const stopIdleAnimation = () => {
    if (idleAnimationRef.current) {
      idleAnimationRef.current.kill()
      idleAnimationRef.current = null

      // Reset all elements
      const allData = containerRef.current?.querySelectorAll('.project-data')
      if (allData) {
        gsap.set(allData, { opacity: 1 })
      }
      projectItemsRef.current.forEach((item) => {
        item?.classList.remove('counter-hidden')
      })
    }
  }

  const startIdleTimer = () => {
    stopIdleTimer()
    idleTimerRef.current = setTimeout(() => {
      if (currentActiveIndexRef.current === -1) {
        startIdleAnimation()
      }
    }, 3000)
  }

  const stopIdleTimer = () => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current)
      idleTimerRef.current = null
    }
  }

  const showBackgroundImage = (imageUrl: string) => {
    const bg = backgroundImageRef.current
    if (!bg) return

    bg.style.transition = 'none'
    bg.style.transform = 'translate(-50%, -50%) scale(1.2)'
    bg.style.backgroundImage = `url(${imageUrl})`
    bg.style.opacity = '1'

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        bg.style.transition =
          'opacity 0.6s ease, transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
        bg.style.transform = 'translate(-50%, -50%) scale(1.0)'
      })
    })
  }

  const hideBackgroundImage = () => {
    if (backgroundImageRef.current) {
      backgroundImageRef.current.style.opacity = '0'
    }
  }

  const updateActiveStates = (activeIndex: number) => {
    currentActiveIndexRef.current = activeIndex
    containerRef.current?.classList.add('has-active')

    projectItemsRef.current.forEach((item, index) => {
      if (item) {
        if (index === activeIndex) {
          item.classList.add('active')
        } else {
          item.classList.remove('active')
        }
      }
    })
  }

  const clearActiveStates = () => {
    currentActiveIndexRef.current = -1
    containerRef.current?.classList.remove('has-active')

    projectItemsRef.current.forEach((item) => {
      if (item) {
        item.classList.remove('active')
        const textElements = item.querySelectorAll('.hover-text')
        const originalTexts = originalTextsRef.current.get(item)
        textElements.forEach((element, i) => {
          gsap.killTweensOf(element)
          if (originalTexts && originalTexts[i]) {
            element.textContent = originalTexts[i]
          }
        })
      }
    })
    startIdleTimer()
  }

  const handleMouseEnter = (index: number) => {
    stopIdleAnimation()
    stopIdleTimer()

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }

    if (currentActiveIndexRef.current === index) return

    updateActiveStates(index)

    const item = projectItemsRef.current[index]
    if (!item) return

    const textElements = item.querySelectorAll('.hover-text')
    const originalTexts = originalTextsRef.current.get(item)

    textElements.forEach((element, i) => {
      gsap.killTweensOf(element)
      if (originalTexts && originalTexts[i]) {
        // Text scramble effect using GSAP timeline
        const finalText = originalTexts[i]
        const chars = 'qwerty1337h@ck3r'
        const duration = 0.8
        const revealDelay = 0.3
        
        // Create a progress object that GSAP can animate
        const obj = { progress: 0 }
        
        gsap.to(obj, {
          progress: 1,
          duration: duration,
          ease: 'none',
          onUpdate: () => {
            const progress = obj.progress
            // Start revealing after revealDelay portion of the animation
            const revealProgress = Math.max(0, (progress - revealDelay) / (1 - revealDelay))
            const revealLength = Math.floor(finalText.length * revealProgress)
            let scrambled = ''

            for (let j = 0; j < finalText.length; j++) {
              if (j < revealLength) {
                scrambled += finalText[j]
              } else {
                scrambled += chars[Math.floor(Math.random() * chars.length)]
              }
            }

            element.textContent = scrambled
          },
          onComplete: () => {
            element.textContent = finalText
          }
        })
      }
    })

    const currentItem = items[index]
    if (currentItem) {
      showBackgroundImage(currentItem.image)
    }
  }

  const handleMouseLeave = (index: number) => {
    debounceTimeoutRef.current = setTimeout(() => {
      const item = projectItemsRef.current[index]
      if (!item) return

      const textElements = item.querySelectorAll('.hover-text')
      const originalTexts = originalTextsRef.current.get(item)

      textElements.forEach((element, i) => {
        gsap.killTweensOf(element)
        if (originalTexts && originalTexts[i]) {
          element.textContent = originalTexts[i]
        }
      })
    }, 50)
  }

  const handleContainerMouseLeave = () => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }
    clearActiveStates()
    hideBackgroundImage()
    startIdleTimer()
  }

  const handleItemClick = (index: number) => {
    const item = items[index]
    if (item && item.isAlbum && item.album) {
      setSelectedAlbum(item.album)
      hideBackgroundImage()
      clearActiveStates()
    }
  }

  const handleBackToAlbums = () => {
    setSelectedAlbum(null)
    hideBackgroundImage()
    clearActiveStates()
  }

  // Start idle timer on mount
  useEffect(() => {
    startIdleTimer()
    return () => {
      stopIdleTimer()
      stopIdleAnimation()
    }
  }, [])

  return (
    <div className="photography-window">
      <main
        className="portfolio-container"
        ref={containerRef}
        onMouseLeave={handleContainerMouseLeave}
      >
        <h1 className="sr-only">{PHOTOGRAPHY_META.title}</h1>

        {selectedAlbum && (
          <div className="album-detail-header">
            <button className="back-btn hover-text" onClick={handleBackToAlbums}>
              {PHOTOGRAPHY_META.backBtnText}
            </button>
            <div className="album-detail-info">
              <h2>{selectedAlbum.title}</h2>
              <p>{selectedAlbum.description}</p>
            </div>
          </div>
        )}

        <ul className="project-list" role="list">
          {items.map((item, index) => (
            <li
              key={`${item.col1}-${index}`}
              className="project-item"
              ref={(el) => {
                projectItemsRef.current[index] = el
              }}
              data-image={item.image}
              onMouseEnter={() => handleMouseEnter(index)}
              onMouseLeave={() => handleMouseLeave(index)}
              onClick={() => handleItemClick(index)}
            >
              <span className="project-data title hover-text">{item.col1}</span>
              <span className="project-data location hover-text">{item.col2}</span>
              <span className="project-data category hover-text">{item.col3}</span>
              <span className="project-data camera hover-text">{item.col4}</span>
              <span className="project-data year hover-text">{item.col5}</span>
            </li>
          ))}
        </ul>
      </main>

      <div
        className="background-image"
        ref={backgroundImageRef}
        role="img"
        aria-hidden="true"
      />

      <aside className="corner-elements">
        <div className="corner-item top-left">
          <div className="corner-square" aria-hidden="true" />
        </div>
        <nav className="corner-item top-right">
          {PHOTOGRAPHY_META.socialLinks.map((link, index) => (
            <span key={link.label}>
              <a href={link.url} target={link.url.startsWith('mailto:') ? undefined : '_blank'} rel={link.url.startsWith('mailto:') ? undefined : 'noopener noreferrer'}>
                {link.label}
              </a>
              {index < PHOTOGRAPHY_META.socialLinks.length - 1 ? ' | ' : ''}
            </span>
          ))}
        </nav>
        <div className="corner-item bottom-left">{PHOTOGRAPHY_META.coordinates}</div>
        <time className="corner-item bottom-right">
          {currentTime.split(':')[0]}
          <span className="time-blink">:</span>
          {currentTime.split(':')[1]}
        </time>
      </aside>
    </div>
  )
}
